import { componentCls } from "@faire/slate/lib/tracking";
import { FormFieldValidationStatusIndicator } from "@faire/slate/private/form/FormFieldValidationStatusIndicator";
import { InputBase } from "@faire/slate/private/form/InputBase";
import { Labelled } from "@faire/slate/private/form/Labelled";
import { buildFormIds } from "@faire/slate/private/form/utils";
import type { IText } from "@faire/slate/private/shared/types";
import type {
  ITextInputWebProps,
  TextInputStatus,
} from "@faire/slate/private/text-input/types";
import { getFormFieldValidationStatusForTextInput } from "@faire/slate/private/text-input/utils";
import React from "react";

import { TEXTINPUT_UNIQUE_ID_PREFIX } from "./consts";

type IProps = {
  /**
   * Whether the text input component is disabled.
   */
  disabled?: boolean;

  /**
   * Controls the error state of the text input component.
   * When error is a `string` or `<StrictLocalMsg>` or `<ServerSideLocalMsg>`, error content
   * is displayed in the helper text.
   */
  error?: boolean | IText;

  /**
   * The helper text of the text input component.
   */
  helper?: IText;

  /**
   * The label of the text input component.
   */
  label?: string;

  /**
   * Whether to append "(optional)" text to the text input label.
   *
   * @default false
   */
  optional?: boolean;

  /**
   * The placeholder of the text input component.
   */
  placeholder?: string;

  /**
   * The tooltip text for the tooltip in the label.
   */
  tooltipText?: IText;

  /**
   * **web**
   * The z-index of the label tooltip.
   *
   * @default 5
   */
  tooltipZIndex?: number;

  /**
   * The value of the text input component.
   */
  value?: string;

  /**
   * The validation state status indicator.
   */
  status?: TextInputStatus;

  // DO NOT ADD any new prop without aligning with Slate team first
} & ITextInputWebProps;

/**
 * Text Inputs allow users enter and edit text.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Text-Input-2242efb5c25a8099bf48d8e968f56e03 | Slate/TextInput}
 */
export const TextInput: React.FC<IProps> = ({
  disabled,
  error: errorProp,
  helper,
  id: idProp,
  label,
  type = "text",
  tooltipText,
  tooltipZIndex,
  optional = false,
  status,
  ...props
}) => {
  const { value } = props;
  const { id, helperId } = buildFormIds({
    prefix: TEXTINPUT_UNIQUE_ID_PREFIX,
    idProp,
    name: props.name,
    label,
    helper,
    error: errorProp,
  });
  const formFieldValidationStatus = getFormFieldValidationStatusForTextInput(
    errorProp,
    status
  );
  const error = status === "loading" ? undefined : errorProp;

  return (
    <Labelled
      className={componentCls()}
      data-component-name="Slate/TextInput"
      disabled={disabled}
      error={error}
      helper={helper}
      helperId={helperId}
      label={label}
      inputId={id}
      tooltipText={tooltipText}
      tooltipZIndex={tooltipZIndex}
      optional={optional}
    >
      <InputBase
        aria-describedby={helperId}
        disabled={disabled}
        error={error}
        id={id}
        type={type}
        suffix={
          <FormFieldValidationStatusIndicator
            status={formFieldValidationStatus}
            value={value}
          />
        }
        {...props}
      />
    </Labelled>
  );
};
